import { LinkButton } from "lib/miolo";
import { useApolloClient, NetworkStatus } from "@apollo/client";
import type { ApolloClient, ApolloQueryResult } from "@apollo/client";
import AssetDiscussionCommentByShortId from "lib/graphql/queries/discussions/AssetDiscussionCommentByShortId";
import type {
  IAssetDiscussionCommentByShortIdQuery,
  IAssetDiscussionCommentByShortIdQueryVariables,
} from "lib/graphql/queries/discussions/__generated__/AssetDiscussionCommentByShortId.generated";
import { useAsyncCallback } from "react-async-hook";
import AssetDiscussionCommentReplies from "../../lib/graphql/queries/discussions/AssetDiscussionCommentReplies";
import DiscussionCommentsByAsset from "../../lib/graphql/queries/discussions/DiscussionCommentsByAsset";
import type {
  IAssetDiscussionCommentRepliesQuery,
  IAssetDiscussionCommentRepliesQueryVariables,
} from "../../lib/graphql/queries/discussions/__generated__/AssetDiscussionCommentReplies.generated";
import type { IDiscussionCommentsByAssetQuery } from "../../lib/graphql/queries/discussions/__generated__/DiscussionCommentsByAsset.generated";

interface IViewMoreRepliesProps {
  assetId: string;
  rootCommentShortId: string;
  repliesPerPage: number;
}

export default function ViewMoreReplies({ assetId, rootCommentShortId, repliesPerPage }: IViewMoreRepliesProps) {
  const client = useApolloClient();

  // TODO(andreyvital): handle `fetchMoreReplies.error` somehow
  const fetchMoreReplies = useAsyncCallback(async (rootCommentShortId: string, cursor: string, limit: number) => {
    const fetchMoreRepliesQueryResponse = await client.query<
      IAssetDiscussionCommentRepliesQuery,
      IAssetDiscussionCommentRepliesQueryVariables
    >({
      query: AssetDiscussionCommentReplies,
      variables: {
        rootCommentShortId,
        cursor,
        limit,
      },
    });

    return fetchMoreRepliesQueryResponse;
  });

  const loadMoreReplies = async (assetId: string) => {
    const repliesCursor = getRootCommentRepliesCursor(client, assetId, rootCommentShortId);

    if (!repliesCursor) {
      return;
    }

    let fetchMoreRepliesQueryResponse: ApolloQueryResult<IAssetDiscussionCommentRepliesQuery> | null = null;

    try {
      fetchMoreRepliesQueryResponse = await fetchMoreReplies.execute(rootCommentShortId, repliesCursor, repliesPerPage);
    } catch (err) {} // eslint-disable-line

    if (!fetchMoreRepliesQueryResponse) {
      return;
    }

    if (fetchMoreRepliesQueryResponse.networkStatus === NetworkStatus.ready && fetchMoreRepliesQueryResponse.errors) {
      return;
    }

    try {
      const discussionCommentsByAssetQuery = client.readQuery<IDiscussionCommentsByAssetQuery>({
        query: DiscussionCommentsByAsset,
        variables: {
          assetId,
        },
      });

      if (!discussionCommentsByAssetQuery) {
        return;
      }

      client.writeQuery<IDiscussionCommentsByAssetQuery>({
        query: DiscussionCommentsByAsset,
        variables: {
          assetId,
        },
        data: {
          __typename: "Query",
          discussionCommentsByAsset: {
            ...discussionCommentsByAssetQuery.discussionCommentsByAsset,
            nodes: discussionCommentsByAssetQuery.discussionCommentsByAsset.nodes.map((node) => {
              // this isn't the comment we're looking for
              if (node.shortId !== rootCommentShortId) {
                return node;
              }

              // if I check for `fetchMoreRepliesQueryResponse.data` outside of the `.map`'s scope - TypeScript
              // will complain that the variable can be null/undefined.
              if (
                !fetchMoreRepliesQueryResponse ||
                !fetchMoreRepliesQueryResponse.data ||
                !fetchMoreRepliesQueryResponse.data.assetDiscussionCommentByShortId
              ) {
                return node;
              }

              const { assetDiscussionCommentByShortId } = fetchMoreRepliesQueryResponse.data;

              return {
                ...node,
                replies: {
                  ...node.replies,
                  ...assetDiscussionCommentByShortId.replies,
                  nodes: node.replies.nodes.concat(assetDiscussionCommentByShortId.replies.nodes),
                },
              };
            }),
          },
        },
      });
    } catch (err) {} // eslint-disable-line

    try {
      const assetDiscussionCommentByShortIdQuery = client.readQuery<
        IAssetDiscussionCommentByShortIdQuery,
        IAssetDiscussionCommentByShortIdQueryVariables
      >({
        query: AssetDiscussionCommentByShortId,
        variables: {
          commentShortId: rootCommentShortId,
        },
      });

      if (
        !assetDiscussionCommentByShortIdQuery ||
        !assetDiscussionCommentByShortIdQuery.assetDiscussionCommentByShortId
      ) {
        return;
      }

      // if I check for `fetchMoreRepliesQueryResponse.data` outside of the `.map`'s scope - TypeScript
      // will complain that the variable can be null/undefined.
      if (
        !fetchMoreRepliesQueryResponse ||
        !fetchMoreRepliesQueryResponse.data ||
        !fetchMoreRepliesQueryResponse.data.assetDiscussionCommentByShortId
      ) {
        return;
      }

      const { assetDiscussionCommentByShortId } = fetchMoreRepliesQueryResponse.data;

      client.writeQuery<IAssetDiscussionCommentByShortIdQuery, IAssetDiscussionCommentByShortIdQueryVariables>({
        query: AssetDiscussionCommentByShortId,
        variables: {
          commentShortId: rootCommentShortId,
        },
        data: {
          __typename: "Query",
          assetDiscussionCommentByShortId: {
            ...assetDiscussionCommentByShortIdQuery.assetDiscussionCommentByShortId,
            replies: {
              ...assetDiscussionCommentByShortIdQuery.assetDiscussionCommentByShortId.replies,
              ...assetDiscussionCommentByShortId.replies,
              nodes: assetDiscussionCommentByShortIdQuery.assetDiscussionCommentByShortId.replies.nodes.concat(
                assetDiscussionCommentByShortId.replies.nodes,
              ),
            },
          },
        },
      });
    } catch (err) {} // eslint-disable-line
  };

  return (
    <LinkButton
      disabled={fetchMoreReplies.loading}
      variant={fetchMoreReplies.loading ? "disabled" : "primary"}
      onClick={() => loadMoreReplies(assetId)}
      css={{
        letterSpacing: 1,
      }}
    >
      {fetchMoreReplies.loading ? "Aguarde..." : "Visualizar mais respostas"}
    </LinkButton>
  );
}

function getRootCommentRepliesCursor(
  apolloClient: ApolloClient<any>,
  assetId: string,
  rootCommentShortId: string,
): string | null {
  const discussionCommentsByAsset = () => {
    try {
      const discussionCommentsByAssetQuery = apolloClient.readQuery<IDiscussionCommentsByAssetQuery>({
        query: DiscussionCommentsByAsset,
        variables: {
          assetId,
        },
      });

      if (!discussionCommentsByAssetQuery) {
        return null;
      }

      const rootComment = discussionCommentsByAssetQuery.discussionCommentsByAsset.nodes.find((node) => {
        return node.shortId === rootCommentShortId;
      });

      if (!rootComment) {
        return null;
      }

      if (!rootComment.replies.pageInfo.hasNextPage) {
        return null;
      }

      return rootComment.replies.pageInfo.cursor;
    } catch (err) {
      return null;
    }
  };

  const assetDiscussionCommentByShortId = () => {
    try {
      const assetDiscussionCommentByShortIdQuery = apolloClient.readQuery<
        IAssetDiscussionCommentByShortIdQuery,
        IAssetDiscussionCommentByShortIdQueryVariables
      >({
        query: AssetDiscussionCommentByShortId,
        variables: {
          commentShortId: rootCommentShortId,
        },
      });

      if (
        !assetDiscussionCommentByShortIdQuery ||
        !assetDiscussionCommentByShortIdQuery.assetDiscussionCommentByShortId ||
        !assetDiscussionCommentByShortIdQuery.assetDiscussionCommentByShortId.replies.pageInfo.hasNextPage
      ) {
        return null;
      }

      return assetDiscussionCommentByShortIdQuery.assetDiscussionCommentByShortId.replies.pageInfo.cursor;
    } catch (err) {
      return null;
    }
  };

  // 1st we try to get the replies cursor from `DiscussionCommentsByAsset` - which's the most common query. If it yields
  // nothing (the query wasn't run), then it should try `AssetDiscussionCommentByShortId` - meaning that the user is
  // viewing the comment in the isolated page.
  const repliesCursor = discussionCommentsByAsset();

  if (repliesCursor) {
    return repliesCursor;
  }

  return assetDiscussionCommentByShortId();
}
